import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faWifi,
    faCircleExclamation,
    faCircleCheck,
    faXmark,
    faSignal,
} from '@fortawesome/free-solid-svg-icons';
import useNetworkStatus from '../../hooks/useNetworkStatus';
import './NetworkStatusNotifier.css';

const VARIANTS = {
    offline: {
        className: 'nsn-offline',
        icon: faCircleExclamation,
        eyebrow: 'Connection lost',
        title: 'You are offline',
        message: "We can't reach the server. We'll keep retrying — your work stays safe locally.",
        defaultMeta: 'No internet connection',
        autoDismissMs: null,
        showProgress: false,
        showRings: true,
    },
    slow: {
        className: 'nsn-slow',
        icon: faWifi,
        eyebrow: 'Degraded link',
        title: 'Slow connection detected',
        message: 'Pages may take longer to load. We are still connected, just throttled.',
        defaultMeta: 'Limited bandwidth',
        autoDismissMs: 6500,
        showProgress: true,
        showRings: true,
    },
    restored: {
        className: 'nsn-restored',
        icon: faCircleCheck,
        eyebrow: 'All systems go',
        title: 'Back online',
        message: 'Your connection is restored. You can resume working normally.',
        defaultMeta: 'Connection restored',
        autoDismissMs: 4500,
        showProgress: true,
        showRings: false,
    },
};

const ACTIVE_STATUSES = new Set(['offline', 'slow', 'restored']);

const formatMeta = (variant, effectiveType, latencyMs) => {
    if (variant === 'offline') return VARIANTS.offline.defaultMeta;
    const parts = [];
    if (effectiveType) parts.push(effectiveType.toUpperCase());
    if (latencyMs != null && Number.isFinite(latencyMs)) {
        parts.push(`${Math.round(latencyMs)} ms`);
    }
    return parts.length ? parts.join(' · ') : VARIANTS[variant].defaultMeta;
};

const NetworkStatusNotifier = () => {
    const { status, effectiveType, latencyMs } = useNetworkStatus();
    const [visibleStatus, setVisibleStatus] = useState(null);
    const [leaving, setLeaving] = useState(false);
    const [manuallyDismissed, setManuallyDismissed] = useState(false);
    const leaveTimerRef = useRef(null);
    const autoDismissTimerRef = useRef(null);

    useEffect(() => {
        if (ACTIVE_STATUSES.has(status)) {
            setManuallyDismissed(false);
            setLeaving(false);
            setVisibleStatus(status);
            if (leaveTimerRef.current) {
                clearTimeout(leaveTimerRef.current);
                leaveTimerRef.current = null;
            }
            return;
        }
        if (visibleStatus) {
            setLeaving(true);
            if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
            leaveTimerRef.current = setTimeout(() => {
                setVisibleStatus(null);
                setLeaving(false);
            }, 320);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);

    useEffect(() => {
        if (autoDismissTimerRef.current) {
            clearTimeout(autoDismissTimerRef.current);
            autoDismissTimerRef.current = null;
        }
        if (!visibleStatus || leaving || manuallyDismissed) return undefined;
        const variant = VARIANTS[visibleStatus];
        if (!variant?.autoDismissMs) return undefined;
        autoDismissTimerRef.current = setTimeout(() => {
            setLeaving(true);
            leaveTimerRef.current = setTimeout(() => {
                setVisibleStatus(null);
                setLeaving(false);
            }, 320);
        }, variant.autoDismissMs);
        return () => {
            if (autoDismissTimerRef.current) clearTimeout(autoDismissTimerRef.current);
        };
    }, [visibleStatus, leaving, manuallyDismissed]);

    useEffect(() => () => {
        if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
        if (autoDismissTimerRef.current) clearTimeout(autoDismissTimerRef.current);
    }, []);

    const handleClose = () => {
        setManuallyDismissed(true);
        setLeaving(true);
        if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
        leaveTimerRef.current = setTimeout(() => {
            setVisibleStatus(null);
            setLeaving(false);
        }, 320);
    };

    if (!visibleStatus) return null;
    if (manuallyDismissed && !leaving) return null;

    const variant = VARIANTS[visibleStatus];
    const meta = formatMeta(visibleStatus, effectiveType, latencyMs);
    const liveMode = visibleStatus === 'offline' ? 'assertive' : 'polite';

    return (
        <div className="nsn-root" aria-live={liveMode} role="status">
            <div
                className={`nsn-card ${variant.className} ${leaving ? 'nsn-leave' : ''}`}
                data-status={visibleStatus}
            >
                <span className="nsn-shimmer" aria-hidden="true" />

                <div className="nsn-icon-rail" aria-hidden="true">
                    {variant.showRings && (
                        <span className="nsn-icon-rings">
                            <span className="nsn-ring nsn-ring-1" />
                            <span className="nsn-ring nsn-ring-2" />
                        </span>
                    )}
                    <FontAwesomeIcon
                        icon={variant.icon}
                        className={`nsn-icon ${visibleStatus !== 'restored' ? 'nsn-icon-pulse' : ''}`}
                    />
                </div>

                <div className="nsn-body">
                    <span className="nsn-eyebrow">{variant.eyebrow}</span>
                    <p className="nsn-title">{variant.title}</p>
                    <p className="nsn-message">{variant.message}</p>
                    <div className="nsn-meta-row">
                        <span className="nsn-chip">
                            <FontAwesomeIcon icon={faSignal} className="nsn-chip-icon" />
                            <span>{meta}</span>
                        </span>
                    </div>
                </div>

                <button
                    type="button"
                    className="nsn-close"
                    onClick={handleClose}
                    aria-label="Dismiss network notification"
                >
                    <FontAwesomeIcon icon={faXmark} />
                </button>

                {variant.showProgress && !leaving && (
                    <div className="nsn-progress" aria-hidden="true">
                        <span
                            className="nsn-progress-bar"
                            style={{ animationDuration: `${variant.autoDismissMs}ms` }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default NetworkStatusNotifier;
